import type { PointerEvent } from 'react';
import { useStrings } from '../../state/useLocale';

/**
 * The keys a phone's keyboard does not have, in a row under the terminal.
 *
 * A shell is driven by Tab, the arrows and Control-C as much as by letters,
 * and none of them is on a phone's keyboard. Each button sends exactly the
 * bytes the key would (`SessionTerminal.send`), so the shell cannot tell a
 * button from a keyboard. The symbols are the ones a phone hides behind a
 * second or third layer, and the first levels type all of them.
 *
 * A press must not take the focus from the terminal: that would close the
 * keyboard the learner is typing on. So the button refuses focus on
 * pointerdown and the press is sent on click as usual, which keeps keyboard
 * and screen-reader use working. Shown only in the phone layout (play.css).
 */
const KEYS: { bytes: string; text: string; label?: 'tab' | 'ctrlC' | 'esc' | 'up' | 'down' | 'left' | 'right' }[] = [
  { bytes: '\t', text: 'Tab', label: 'tab' },
  { bytes: '\x03', text: '^C', label: 'ctrlC' },
  { bytes: '\x1b[A', text: '↑', label: 'up' },
  { bytes: '\x1b[B', text: '↓', label: 'down' },
  { bytes: '\x1b[D', text: '←', label: 'left' },
  { bytes: '\x1b[C', text: '→', label: 'right' },
  { bytes: '|', text: '|' },
  { bytes: '>', text: '>' },
  { bytes: '~', text: '~' },
  { bytes: '/', text: '/' },
  { bytes: '-', text: '-' },
  { bytes: '\x1b', text: 'Esc', label: 'esc' },
];

export function PhoneKeys({ onKey }: { onKey(bytes: string): void }) {
  const s = useStrings().play.phone;
  const keep = (e: PointerEvent<HTMLButtonElement>) => e.preventDefault();
  return (
    <div className="phone-keys" role="toolbar" aria-label={s.keys} dir="ltr">
      {KEYS.map((k) => (
        <button
          key={k.text}
          type="button"
          className="phone-key"
          aria-label={k.label ? s[k.label] : undefined}
          onPointerDown={keep}
          onClick={() => onKey(k.bytes)}
        >
          {k.text}
        </button>
      ))}
    </div>
  );
}

export default PhoneKeys;
